import {
  type ReconcileSchedulingPatientRequest,
  type ReconcileSchedulingPatientResponse,
  schedulingPatientMatchUnresolved,
} from "@emr/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { intakeErrorMessage } from "../../api/schedulingIntake";
import { PatientLinkSlip } from "../../components/PatientLinkSlip";
import { WALK_IN_CODE_IDLE_MS } from "../../components/WalkInIntakeCode";
import { useDemoScheduling } from "../../demo/DemoSchedulingProvider";
import {
  appointmentOpenedDetailsLink,
  demoPublicBookingUrl,
  patientAppOriginConfigured,
} from "../../demo/demoSchedulingClient";
import { schedulingErrorDetail } from "../../demo/schedulingErrors";
import { IntakeChartReconciler } from "./IntakeChartReconciler";
import { type IntakeReviewClient, schedulingPatientMatchesKey } from "./intakeReviewQueries";
import { ScheduleModal } from "./ScheduleModal";
import { formatInClinic } from "./scheduleTime";

const CHART_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** The booking review's routes: the one match read and reconcile path, and the chart search. */
export type BookingReviewClient = Pick<
  IntakeReviewClient,
  "getSchedulingPatientMatches" | "reconcileSchedulingPatient" | "listPatientDirectory"
>;

/** A refusal of "Ask for patient details", with the detail the dialog reacts to. */
interface AskRefusal {
  readonly message: string;
  readonly detail: string | undefined;
}

type Phase =
  | { readonly kind: "review" }
  /** The questionnaire link from "Ask for patient details", only while the code is on screen. */
  | { readonly kind: "code"; readonly url: string }
  | { readonly kind: "hidden"; readonly reason: "idle" | "answered" };

/**
 * Whether Review booking is where this booking's chart is matched (PA-10): a returning patient's
 * durable booking without a chart, before arrival, with no questionnaire in. A booking whose
 * questionnaire is in gets its chart in Review intake instead (PA-6), and any other booking keeps
 * Schedule's chart search.
 */
export function opensBookingReview(appointment: {
  readonly version?: number | undefined;
  readonly status: string;
  readonly patient: { readonly kind: string; readonly patientId?: string | undefined };
  readonly intakeReview?: unknown;
}): boolean {
  return (
    appointment.version !== undefined &&
    appointment.patient.kind === "returning" &&
    appointment.patient.patientId === undefined &&
    ["booked", "confirmed"].includes(appointment.status) &&
    appointment.intakeReview === undefined
  );
}

/**
 * "Review booking" (PA-10 / EXECDEV-217): a returning patient booked online and their booking has
 * no chart yet. One Schedule dialog for Schedule's side panel and Today's Expected rows.
 *
 * - The charts matched when they booked (their health card, or their name, date of birth and
 *   phone), through PA-6's one match read and reconcile path (IntakeChartReconciler): **Link this
 *   chart**, **Not this patient** (a rule-out that keeps the patient's links working), or any
 *   chart in the clinic by name or MRN. A chart the patient's health card matched under another
 *   date of birth is linked only by correcting the booking's date of birth, after an in-place
 *   confirmation; the server audits the correction.
 * - Once no matched chart is left to decide on: **Ask for patient details**. The booking's links
 *   are issued again with a questionnaire that asks every section, shown as PA-5's QR slip; the
 *   earlier links stop working. The answers arrive in Review intake, where the chart is created
 *   or linked behind the duplicate check. A patient without a phone is registered on Patients and
 *   then linked here.
 * - Once the patient opened that link (the schedule read's openedDetailsLink), the dialog says
 *   they may be answering instead, and a new code replaces the link only after an in-place
 *   confirmation (lead decision H7, as PA-5's walk-in codes); the server refuses an unconfirmed
 *   replacement too (intake_link_opened).
 * - Escape first backs out of a question asked in place (this one, or the reconciler's date of
 *   birth correction) before it closes the dialog.
 *
 * The matches are an audited read, fetched when the dialog opens and again after a change made
 * here, never on the schedule poll.
 */
export function BookingReviewDialog({
  appointmentId,
  client,
  siteId,
  onClose,
  onOpenChart,
  onReviewIntake,
}: {
  readonly appointmentId: string;
  readonly client: BookingReviewClient;
  readonly siteId: string;
  readonly onClose: () => void;
  readonly onOpenChart?: ((patientId: string, displayName: string) => void) | undefined;
  /** Opens the questionnaire's review, once the patient's answers are in. */
  readonly onReviewIntake?: ((appointmentId: string) => void) | undefined;
}) {
  const scheduling = useDemoScheduling();
  const queryClient = useQueryClient();
  const appointment = scheduling.state?.appointments.find(
    (candidate) => candidate.id === appointmentId,
  );
  const timeZone = scheduling.state?.configuration.timeZone ?? "America/Toronto";
  const clinicName = scheduling.state?.configuration.clinicName ?? "Your clinic";
  const refreshSchedule = scheduling.refresh;
  const issueTokens = scheduling.issuePublicTokens;
  const [phase, setPhase] = useState<Phase>({ kind: "review" });
  const [notice, setNotice] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [askRefusal, setAskRefusal] = useState<AskRefusal | null>(null);
  /** The desk asked to replace the questionnaire link the patient opened: confirm first (H7). */
  const [confirmReplace, setConfirmReplace] = useState(false);
  const askHeadingId = useId();
  const codeRoot = useRef<HTMLDivElement>(null);
  const newCodeButton = useRef<HTMLButtonElement>(null);
  const keepButton = useRef<HTMLButtonElement>(null);
  const refocusNewCode = useRef(false);
  // Escape first backs out of a question the chart reconciler asks in place.
  const reconcilerEscape = useRef<(() => boolean) | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const name = appointment?.patient.displayName ?? "This booking";
  const answered =
    appointment !== undefined &&
    (appointment.intakeStatus === "completed" || appointment.intakeReview !== undefined);
  /** The patient opened the link from "Ask for patient details" and may be answering. */
  const openedLink =
    appointment === undefined ? undefined : appointmentOpenedDetailsLink(appointment);
  const replacing = confirmReplace && openedLink !== undefined;
  // Focus: the safe choice when the question opens; back to "Show a new code…" when it is kept.
  useEffect(() => {
    if (replacing) {
      keepButton.current?.focus();
    } else if (refocusNewCode.current) {
      refocusNewCode.current = false;
      newCodeButton.current?.focus();
    }
  }, [replacing]);

  /**
   * After a change: the schedule's pill and the review list catch up. A rule-out keeps the
   * appointment's version, so the reconciler re-reads its matches itself (an audited read, so
   * never twice); a link replaces the matches with the linked chart.
   */
  const refreshAfterChange = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["scheduling-intake-reviews", siteId] }),
      refreshSchedule().catch(() => undefined),
    ]);
  }, [queryClient, refreshSchedule, siteId]);

  /*
   * Another desk may link or rule out meanwhile: the 4-second schedule poll then brings a new
   * match state without a new version, so the audited matches are read again, once. A change made
   * here records the state it led to first, so it is not read twice.
   */
  const matchState = appointment?.patientMatch?.state;
  const seenMatchState = useRef(matchState);
  useEffect(() => {
    if (seenMatchState.current === matchState) return;
    seenMatchState.current = matchState;
    void queryClient.invalidateQueries({
      queryKey: schedulingPatientMatchesKey(siteId, appointmentId),
    });
  }, [appointmentId, matchState, queryClient, siteId]);

  const reconciled = async (
    result: ReconcileSchedulingPatientResponse,
    request: ReconcileSchedulingPatientRequest,
  ) => {
    seenMatchState.current = result.appointment.patientMatch?.state;
    setNotice(
      result.outcome === "mismatch"
        ? "Marked as not this patient. No chart was linked."
        : request.action === "link" && request.correctBookingDateOfBirth === true
          ? "Linked the chart and gave the booking the chart's date of birth."
          : "Linked the chart to this booking.",
    );
    await refreshAfterChange();
  };

  // The code leaves the screen once the answers are in, or after a quiet spell (PA-5's rule).
  useEffect(() => {
    if (phase.kind === "code" && answered) setPhase({ kind: "hidden", reason: "answered" });
  }, [answered, phase.kind]);
  const idleTimer = useRef<number | undefined>(undefined);
  const restartIdle = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(
      () => setPhase({ kind: "hidden", reason: "idle" }),
      WALK_IN_CODE_IDLE_MS,
    );
  }, []);
  useEffect(() => {
    if (phase.kind !== "code") return;
    restartIdle();
    return () => window.clearTimeout(idleTimer.current);
  }, [phase.kind, restartIdle]);
  // Focus follows the content when the code or its outcome replaces the review.
  useEffect(() => {
    if (phase.kind !== "review") codeRoot.current?.focus();
  }, [phase.kind]);

  const version = appointment?.version;
  const linksEnabled = scheduling.state?.patientLinksEnabled !== false;
  const canAsk =
    issueTokens !== undefined &&
    version !== undefined &&
    linksEnabled &&
    patientAppOriginConfigured() &&
    !answered;

  /** Issues the questionnaire link; replacing one the patient opened names it (H7). */
  const ask = async (replaceOpenedLinkId?: string) => {
    if (asking || !canAsk || appointment === undefined || version === undefined) return;
    setAsking(true);
    setAskRefusal(null);
    try {
      const links = await issueTokens(
        appointment.id,
        version,
        replaceOpenedLinkId === undefined
          ? { patientDetails: true }
          : { patientDetails: true, replaceOpenedLinkId },
      );
      if (!mounted.current) return;
      if (links.intakeQuestionnaireUrl === undefined) {
        setAskRefusal({
          message: "The questionnaire link could not be made. Try again.",
          detail: undefined,
        });
        return;
      }
      setConfirmReplace(false);
      setPhase({ kind: "code", url: demoPublicBookingUrl(links.intakeQuestionnaireUrl) });
    } catch (caught) {
      const detail = schedulingErrorDetail(caught);
      if (detail === "intake_link_opened") {
        // The patient opened the link since this screen last read the schedule: read it, then
        // ask before replacing it.
        await refreshSchedule().catch(() => undefined);
        if (mounted.current) setConfirmReplace(true);
      } else if (
        detail === "patient_match_review_required" ||
        detail === "intake_already_completed" ||
        detail === "appointment_changed"
      ) {
        // Another desk changed the booking meanwhile: the new state re-reads the matches.
        await refreshAfterChange();
      }
      if (mounted.current)
        setAskRefusal({
          message: intakeErrorMessage(caught, "The questionnaire link could not be made."),
          detail,
        });
    } finally {
      if (mounted.current) setAsking(false);
    }
  };

  /** Keeps the link the patient opened: backs out of the question, never mid-request. */
  const keepLink = () => {
    if (asking) return;
    refocusNewCode.current = true;
    setConfirmReplace(false);
    setAskRefusal(null);
  };

  const askForDetails = (openMatches: number): ReactNode => {
    // The refusal stays on screen even when the matches it points to replace the button; the
    // replace question already says what an opened link's refusal would.
    const refusal =
      askRefusal === null || (replacing && askRefusal.detail === "intake_link_opened") ? null : (
        <p className="schedule-modal-error" role="alert">
          {askRefusal.message}
        </p>
      );
    if (!canAsk) {
      return (
        <p className="intake-review-note">
          For a patient without a chart in this clinic, register them on the Patients page, then
          find and link their chart here.
        </p>
      );
    }
    if (openMatches > 0 || schedulingPatientMatchUnresolved(appointment?.patientMatch)) {
      return (
        <>
          <p className="intake-review-note">
            If none of these charts is the patient, mark them as not this patient, then ask the
            patient for their details.
          </p>
          {refusal}
        </>
      );
    }
    if (openedLink !== undefined) {
      const openedAt = formatInClinic(openedLink.openedAt, timeZone, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
      if (replacing) {
        return (
          <fieldset className="intake-review-confirm">
            <legend>Replace the link the patient opened?</legend>
            <p>
              {name} opened the questionnaire link on their phone ({openedAt}) and may be answering
              now.
            </p>
            <p>
              A new code stops that link, and answers they have not sent yet are lost. Replace it
              only if they cannot finish on that phone.
            </p>
            <div className="intake-review-actions">
              <button
                ref={keepButton}
                type="button"
                className="schedule-btn ghost"
                disabled={asking}
                onClick={keepLink}
              >
                Keep the current link
              </button>
              <button
                type="button"
                className="schedule-btn primary"
                disabled={asking}
                onClick={() => void ask(openedLink.id)}
              >
                {asking ? "Making a code…" : "Replace with a new code"}
              </button>
            </div>
            {refusal}
          </fieldset>
        );
      }
      return (
        <section className="intake-review-ask" aria-labelledby={askHeadingId}>
          <h4 id={askHeadingId}>Patient details asked</h4>
          <p className="walk-in-code-status" role="status">
            <span aria-hidden="true" />
            Opened on the patient's phone ({openedAt}). Waiting for their answers, which appear in
            Review intake, where you create or link the chart.
          </p>
          <div className="intake-review-actions">
            <button
              ref={newCodeButton}
              type="button"
              className="schedule-btn ghost"
              disabled={asking}
              onClick={() => {
                setAskRefusal(null);
                setConfirmReplace(true);
              }}
            >
              Show a new code…
            </button>
          </div>
          {refusal}
        </section>
      );
    }
    return (
      <section className="intake-review-ask" aria-labelledby={askHeadingId}>
        <h4 id={askHeadingId}>Ask for patient details</h4>
        <p className="intake-review-note">
          The patient scans a code and answers the questionnaire, their details and health card
          included. Their answers appear in Review intake, where you create or link the chart. Their
          earlier links for this booking stop working.
        </p>
        <div className="intake-review-actions">
          <button
            type="button"
            className="schedule-btn primary"
            disabled={asking}
            onClick={() => void ask()}
          >
            {asking ? "Making a code…" : "Ask for patient details"}
          </button>
        </div>
        <p className="intake-review-note">
          If the patient can't use a phone, register them on the Patients page, then find and link
          their chart here.
        </p>
        {refusal}
      </section>
    );
  };

  const description =
    appointment === undefined
      ? undefined
      : [
          name,
          `DOB ${appointment.patient.dateOfBirth}`,
          `visit ${formatInClinic(appointment.startAt, timeZone, { dateStyle: "medium", timeStyle: "short" })}`,
        ].join(" · ");

  if (phase.kind !== "review") {
    const done = (
      <button type="button" className="schedule-btn primary" onClick={onClose}>
        Done
      </button>
    );
    return (
      <ScheduleModal
        eyebrow="Online booking"
        title="Review booking"
        description={description}
        onClose={onClose}
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: notices activity to keep the code up; a direct child of the dialog so its slip prints alone */}
        <div
          ref={codeRoot}
          className="walk-in-code"
          tabIndex={-1}
          onPointerDown={() => (phase.kind === "code" ? restartIdle() : undefined)}
          onKeyDown={() => (phase.kind === "code" ? restartIdle() : undefined)}
        >
          {phase.kind === "code" ? (
            <>
              <p className="walk-in-code-instructions">
                Ask {name} to scan this with their phone camera. They confirm their date of birth,
                then answer about you, coverage and health questions.
              </p>
              <PatientLinkSlip clinicName={clinicName} url={phase.url} />
              <p className="walk-in-code-status" role="status">
                <span aria-hidden="true" />
                Waiting for the patient's answers. They appear in Review intake, where you create or
                link the chart. Their earlier links for this booking no longer work.
              </p>
              <div className="schedule-modal-actions">
                <button type="button" className="schedule-btn ghost" onClick={() => window.print()}>
                  Print slip
                </button>
                {done}
              </div>
            </>
          ) : phase.reason === "answered" ? (
            <>
              <p className="walk-in-code-note" role="status">
                <strong>Answers received.</strong> Review them to create or link {name}'s chart.
                Nothing reaches a chart until someone applies it.
              </p>
              <div className="schedule-modal-actions">
                {onReviewIntake === undefined ? (
                  done
                ) : (
                  <>
                    <button type="button" className="schedule-btn ghost" onClick={onClose}>
                      Done
                    </button>
                    <button
                      type="button"
                      className="schedule-btn primary"
                      onClick={() => onReviewIntake(appointmentId)}
                    >
                      Review intake
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="walk-in-code-note" role="status">
                The code was hidden after 3 minutes without use. The patient can still use it if
                they scanned it.
              </p>
              <div className="schedule-modal-actions">{done}</div>
            </>
          )}
        </div>
      </ScheduleModal>
    );
  }

  let body: ReactNode;
  if (appointment === undefined) {
    body = <p className="intake-review-note">This booking is no longer on the schedule.</p>;
  } else if (appointment.patient.patientId !== undefined) {
    const patientId = appointment.patient.patientId;
    body = (
      <div className="intake-review-linked">
        <p>This booking is linked to a chart. Check the patient in on Schedule when they arrive.</p>
        {onOpenChart !== undefined && CHART_ID.test(patientId) ? (
          <button
            type="button"
            className="schedule-btn ghost sm"
            onClick={() => onOpenChart(patientId, name)}
          >
            Open chart
          </button>
        ) : null}
      </div>
    );
  } else if (!["booked", "confirmed"].includes(appointment.status)) {
    body = (
      <p className="intake-review-note">
        No chart is linked. A chart can be linked only before the patient arrives.
      </p>
    );
  } else if (answered) {
    body = (
      <div className="intake-review-linked">
        <p>The patient's questionnaire is in: link or create the chart in Review intake.</p>
        {onReviewIntake === undefined ? null : (
          <button
            type="button"
            className="schedule-btn primary sm"
            onClick={() => onReviewIntake(appointmentId)}
          >
            Review intake
          </button>
        )}
      </div>
    );
  } else {
    body = (
      <IntakeChartReconciler
        appointmentId={appointmentId}
        expectedVersion={appointment.version}
        identityAvailable={false}
        bookingDateOfBirth={appointment.patient.dateOfBirth}
        timeZone={timeZone}
        client={client}
        siteId={siteId}
        askForDetails={askForDetails}
        escapeHandler={reconcilerEscape}
        onReconciled={reconciled}
      />
    );
  }

  return (
    <ScheduleModal
      eyebrow="Online booking"
      title="Review booking"
      description={description}
      onEscape={() => {
        if (replacing) {
          keepLink();
          return true;
        }
        return reconcilerEscape.current?.() === true;
      }}
      onClose={onClose}
    >
      <div className="intake-review">
        {body}
        {notice ? (
          <p className="intake-review-notice" role="status">
            {notice}
          </p>
        ) : null}
      </div>
      <div className="schedule-modal-actions">
        <button type="button" className="schedule-btn ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </ScheduleModal>
  );
}
